import { useState, useRef, useEffect, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Mic, MicOff, PhoneOff, Sparkles, AlertTriangle, HelpCircle,
  MessageSquare, BookOpen, ShoppingCart, Headphones, Trash2, Loader2, CheckCircle2,
  ShieldCheck,
} from 'lucide-react';

const CHUNK_MS = 15000;  // user choice: 15 s chunks

const CASE_LABEL = { sales: 'Vertrieb', support: 'Support', mixed: 'Gemischt' };
const CASE_BG = {
  sales: 'bg-blue-50 text-blue-700 border-blue-200',
  support: 'bg-amber-50 text-amber-700 border-amber-200',
  mixed: 'bg-gray-100 text-gray-700 border-gray-200',
};

const CONSENT_SCRIPT = "„Ich nutze zur Unterstützung unseres Gesprächs ein internes Assistenzsystem, " +
  "das mir live hilft. Es wird nichts aufgezeichnet. Ist das für Sie in Ordnung?“";

export default function PhoneAssistantPage() {
  const [session, setSession] = useState(null);
  const [recording, setRecording] = useState(false);
  const [processingChunk, setProcessingChunk] = useState(false);
  const [error, setError] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [transferring, setTransferring] = useState(null);
  const [consent, setConsent] = useState(false);
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunkTimerRef = useRef(null);
  const transcriptRef = useRef(null);

  // Derived values — declared BEFORE any hook that depends on them (TDZ-safe)
  const live = session?.liveContext;
  const summary = session?.summary;
  const isFinalized = session?.state === 'finalized';

  // Autoscroll transcript as new text arrives
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [session?.transcriptText]);

  const cleanupRecorder = useCallback(() => {
    if (chunkTimerRef.current) { clearInterval(chunkTimerRef.current); chunkTimerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_e) { /* noop */ }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanupRecorder(), [cleanupRecorder]);

  const sendChunk = useCallback(async (blob, sid) => {
    if (!blob || blob.size < 2000) return;
    const form = new FormData();
    const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
    form.append('audio', blob, `chunk.${ext}`);
    setProcessingChunk(true);
    try {
      const res = await authFetch(`/api/phone-assistant/sessions/${sid}/chunk`, {
        method: 'POST', body: form,
      });
      if (res.ok) {
        const d = await res.json();
        setSession(prev => prev ? {
          ...prev,
          transcriptText: d.transcriptText,
          liveContext: d.liveContext,
        } : prev);
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Chunk-Upload fehlgeschlagen');
      }
    } catch (e) {
      setError(e.message || 'Netzwerkfehler beim Chunk-Upload');
    }
    setProcessingChunk(false);
  }, []);

  const start = async () => {
    setError(null);
    if (!consent) {
      setError('Bitte bestätige zuerst, dass die Einwilligung des Kunden vorliegt.');
      return;
    }
    try {
      const res = await authFetch('/api/phone-assistant/sessions', { method: 'POST' });
      if (!res.ok) { setError('Session konnte nicht gestartet werden'); return; }
      const newSession = await res.json();
      setSession(newSession);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', ''];
      let chosen = '';
      for (const m of mimes) {
        if (!m || (window.MediaRecorder && MediaRecorder.isTypeSupported(m))) { chosen = m; break; }
      }
      const rec = chosen ? new MediaRecorder(stream, { mimeType: chosen }) : new MediaRecorder(stream);
      recorderRef.current = rec;

      const chunks = [];
      rec.ondataavailable = e => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      rec.onstop = () => {
        if (!chunks.length) return;
        const blob = new Blob(chunks.splice(0, chunks.length), { type: rec.mimeType || 'audio/webm' });
        sendChunk(blob, newSession.id);
      };

      rec.start();
      setRecording(true);

      chunkTimerRef.current = setInterval(() => {
        if (rec.state === 'recording') {
          rec.stop();
          setTimeout(() => {
            if (streamRef.current && recorderRef.current === rec) {
              try { rec.start(); } catch (e) { console.warn('restart rec', e); }
            }
          }, 120);
        }
      }, CHUNK_MS);
    } catch (e) {
      setError(e.message || 'Mikrofonzugriff verweigert');
      cleanupRecorder();
      setRecording(false);
    }
  };

  const stop = async () => {
    cleanupRecorder();
    setRecording(false);
    setFinalizing(true);
    await new Promise(r => setTimeout(r, 800));
    if (!session) { setFinalizing(false); return; }
    try {
      const res = await authFetch(`/api/phone-assistant/sessions/${session.id}/finalize`, { method: 'POST' });
      if (res.ok) setSession(await res.json());
      else {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Beenden fehlgeschlagen');
      }
    } finally { setFinalizing(false); }
  };

  const discard = async () => {
    if (!session) return;
    if (!window.confirm('Session verwerfen? Alle Daten gehen verloren.')) return;
    cleanupRecorder();
    setRecording(false);
    await authFetch(`/api/phone-assistant/sessions/${session.id}`, { method: 'DELETE' });
    setSession(null); setError(null); setConsent(false); setMatches([]);
  };

  const toInquiry = async () => {
    if (!session) return;
    setTransferring('inquiry');
    const res = await authFetch(`/api/phone-assistant/sessions/${session.id}/to-inquiry`, { method: 'POST' });
    setTransferring(null);
    if (res.ok) {
      const d = await res.json();
      setSession(prev => ({ ...prev, linkedInquiryId: d.inquiryId }));
      alert('In Anfragen / Angebote übernommen. Dort weiter bearbeiten.');
    } else {
      const d = await res.json().catch(() => ({}));
      setError(d.detail || 'Übernahme fehlgeschlagen');
    }
  };

  const loadMatches = useCallback(async (sid) => {
    setLoadingMatches(true);
    const res = await authFetch(`/api/phone-assistant/sessions/${sid}/match-inquiries`);
    setLoadingMatches(false);
    if (res.ok) {
      const d = await res.json();
      setMatches(d.matches || []);
    }
  }, []);

  useEffect(() => {
    if (isFinalized && session?.id && !session?.linkedInquiryId) {
      loadMatches(session.id);
    }
  }, [isFinalized, session?.id, session?.linkedInquiryId, loadMatches]);

  const [selectedDepartment, setSelectedDepartment] = useState('');

  const appendToInquiry = async (inquiryId) => {
    setTransferring('inquiry');
    const res = await authFetch(`/api/phone-assistant/sessions/${session.id}/append-to-inquiry`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inquiryId, targetDepartment: selectedDepartment || null }),
    });
    setTransferring(null);
    if (res.ok) {
      setSession(prev => ({ ...prev, linkedInquiryId: inquiryId }));
      alert('An bestehende Anfrage angehängt.');
    } else {
      const d = await res.json().catch(() => ({}));
      setError(d.detail || 'Anhängen fehlgeschlagen');
    }
  };

  const toTicket = async () => {
    if (!session) return;
    setTransferring('ticket');
    const res = await authFetch(`/api/phone-assistant/sessions/${session.id}/to-ticket`, { method: 'POST' });
    setTransferring(null);
    if (res.ok) {
      const d = await res.json();
      setSession(prev => ({ ...prev, linkedTicketId: d.ticketId }));
      alert('Als Supportfall angelegt.');
    } else {
      const d = await res.json().catch(() => ({}));
      setError(d.detail || 'Anlage fehlgeschlagen');
    }
  };

  return (
    <div className="flex h-full bg-[#f5f6f7]" data-testid="phone-assistant-page">
      {/* Left: control + collected data */}
      <div className="w-[300px] border-r border-gray-200 bg-white flex flex-col shrink-0 overflow-y-auto">
        <div className="p-4 border-b border-gray-100">
          <h1 className="text-lg font-bold text-[#32373c] flex items-center gap-2">
            <Mic className="w-5 h-5 text-[#9ec043]" /> Telefonassistent
          </h1>
          <p className="text-[11px] text-gray-400 mt-1">Live-Unterstützung · keine Aufzeichnung</p>
        </div>

        <div className="p-4 space-y-3">
          {!session && (
            <>
              {/* DSGVO consent — mandatory before starting */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900"
                   data-testid="phone-consent-banner">
                <div className="flex items-start gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                  <span className="font-semibold">DSGVO-Einwilligung</span>
                </div>
                <p className="mb-2 leading-relaxed text-amber-900/90">
                  Lies dem Anrufer bitte zu Beginn vor:
                </p>
                <p className="italic bg-white border border-amber-200 rounded p-2 leading-relaxed">
                  {CONSENT_SCRIPT}
                </p>
                <label className="flex items-start gap-2 mt-2 cursor-pointer select-none">
                  <Checkbox checked={consent} onCheckedChange={v => setConsent(!!v)}
                            className="mt-0.5"
                            data-testid="phone-consent-checkbox" />
                  <span>Kundeneinwilligung liegt vor</span>
                </label>
              </div>
              <Button onClick={start} disabled={!consent}
                      className="w-full bg-[#9ec043] hover:bg-[#8aad36] text-white h-11 disabled:bg-gray-200 disabled:text-gray-400"
                      data-testid="btn-phone-start">
                <Mic className="w-4 h-4 mr-2" /> Gespräch starten
              </Button>
              {!consent && (
                <p className="text-[10px] text-gray-400 text-center">
                  Ohne Einwilligungs-Häkchen startet das Mikrofon nicht.
                </p>
              )}
            </>
          )}
          {session && recording && (
            <>
              <div className="flex items-center gap-2 text-sm font-semibold text-red-600"
                   data-testid="phone-recording-indicator">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                Live-Mithören aktiv
              </div>
              <div className="text-[10px] text-gray-400">
                Rohdaten und Transkript werden NICHT gespeichert — nur die finale Zusammenfassung.
              </div>
              <Button onClick={stop} className="w-full bg-red-600 hover:bg-red-700 text-white h-11"
                      data-testid="btn-phone-stop">
                <PhoneOff className="w-4 h-4 mr-2" /> Gespräch beenden
              </Button>
              <Button variant="outline" onClick={discard}
                      className="w-full h-9 text-xs text-red-600 border-red-200"
                      data-testid="btn-phone-discard">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Verwerfen
              </Button>
            </>
          )}
          {session && !recording && !isFinalized && (
            <>
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-500">
                <MicOff className="w-4 h-4" /> Aufnahme pausiert
              </div>
              <Button onClick={stop} disabled={finalizing}
                      className="w-full bg-[#9ec043] hover:bg-[#8aad36] text-white h-11"
                      data-testid="btn-phone-finalize">
                {finalizing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Zusammenfassung erzeugen
              </Button>
            </>
          )}
          {isFinalized && (
            <div className="flex items-center gap-2 text-sm font-semibold text-[#7ca735]"
                 data-testid="phone-finalized-indicator">
              <CheckCircle2 className="w-4 h-4" /> Gespräch abgeschlossen
            </div>
          )}
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2"
                 data-testid="phone-error">{error}</div>
          )}
        </div>

        {/* Chips: case type + product families + collected data */}
        {(live || summary) && (
          <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-3">
            <ContextChips live={live} summary={summary} />
          </div>
        )}
      </div>

      {/* Center: KI-Hilfe (wider, primary focus) */}
      <div className="flex-1 flex flex-col min-w-0 border-r border-gray-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#9ec043]" /> KI-Hilfe (live)
          </h2>
          {processingChunk && (
            <span className="text-[10px] text-gray-400 flex items-center gap-1"
                  data-testid="phone-chunk-processing">
              <Loader2 className="w-3 h-3 animate-spin" /> analysiere …
            </span>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="phone-assistance-panel">
          {!session && (
            <EmptyAssistance />
          )}
          {isFinalized && summary ? (
            <SummaryBlock summary={summary} />
          ) : session && (
            <LiveBlock live={live} />
          )}
        </div>

        {isFinalized && summary && (
          <div className="p-4 border-t border-gray-100 bg-gray-50 space-y-2"
               data-testid="phone-summary-actions">
            {!session.linkedInquiryId && matches.length > 0 && (
              <div className="mb-3 bg-white border border-[#9ec043]/30 rounded-lg p-2.5"
                   data-testid="phone-match-suggestions">
                <div className="text-[10px] uppercase tracking-wide text-[#7ca735] font-semibold mb-1.5">
                  Passende offene Anfragen gefunden
                </div>
                <div className="mb-2 flex items-center gap-2 text-[11px]">
                  <span className="text-gray-500">an Abteilung:</span>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="h-6 border border-gray-200 rounded px-1 text-[11px]"
                    data-testid="phone-target-department"
                  >
                    <option value="">— keine Zuordnung —</option>
                    <option value="innendienst">Innendienst</option>
                    <option value="team">Team</option>
                    <option value="produktion">Produktion</option>
                    <option value="technik">Technik</option>
                    <option value="lager">Lager</option>
                    <option value="vertrieb">Vertrieb</option>
                  </select>
                  <span className="text-[10px] text-gray-400 italic">
                    Alle Mitarbeiter dieser Rolle erhalten eine Benachrichtigung.
                  </span>
                </div>
                <div className="space-y-1.5">
                  {matches.slice(0, 3).map(m => (
                    <div key={m.inquiry.id}
                         className="flex items-center gap-2 text-xs p-1.5 rounded bg-[#9ec043]/5">
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium text-[#32373c]">{m.inquiry.subject}</div>
                        <div className="text-[10px] text-gray-500 truncate">
                          {m.inquiry.customerName} · {m.matchReasons.join(' · ')}
                        </div>
                      </div>
                      <Button onClick={() => appendToInquiry(m.inquiry.id)}
                              disabled={transferring === 'inquiry'}
                              className="h-7 text-[11px] bg-[#9ec043] hover:bg-[#8aad36] text-white px-2"
                              data-testid={`btn-phone-append-${m.inquiry.id}`}>
                        Anhängen
                      </Button>
                    </div>
                  ))}
                </div>
                {loadingMatches && (
                  <div className="text-[10px] text-gray-400 mt-1">Suche läuft …</div>
                )}
              </div>
            )}
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {session.linkedInquiryId ? 'Bereits verknüpft' : 'Übernehmen in …'}
            </h3>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={toInquiry} disabled={transferring === 'inquiry' || !!session.linkedInquiryId}
                      className="bg-[#9ec043] hover:bg-[#8aad36] text-white h-9 text-xs"
                      data-testid="btn-phone-to-inquiry">
                <ShoppingCart className="w-3.5 h-3.5 mr-1" />
                {session.linkedInquiryId ? 'Verknüpft' : 'Neue Anfrage / Angebot'}
              </Button>
              <Button onClick={toTicket} disabled={transferring === 'ticket' || !!session.linkedTicketId}
                      variant="outline" className="h-9 text-xs"
                      data-testid="btn-phone-to-ticket">
                <Headphones className="w-3.5 h-3.5 mr-1" />
                {session.linkedTicketId ? 'Bereits angelegt' : 'Support-Ticket'}
              </Button>
              <Button onClick={discard} variant="outline"
                      className="h-9 text-xs text-red-600 border-red-200 ml-auto"
                      data-testid="btn-phone-close">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Schließen
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Right: Live transcript */}
      <div className="w-[340px] bg-white flex flex-col shrink-0">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-gray-400" /> Live-Mitschrift
          </h2>
          <p className="text-[10px] text-gray-400 mt-0.5">Wird nicht gespeichert</p>
        </div>
        <div ref={transcriptRef}
             className="flex-1 overflow-y-auto p-4 text-xs text-gray-700 leading-relaxed whitespace-pre-wrap font-sans bg-gray-50/50"
             data-testid="phone-transcript">
          {isFinalized ? (
            <em className="text-gray-400">Mitschrift wurde nach Ende des Gesprächs entfernt (DSGVO). Die KI-Zusammenfassung findest du in der Mitte.</em>
          ) : (
            session?.transcriptText || <em className="text-gray-400">Noch nichts gesagt worden …</em>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyAssistance() {
  return (
    <div className="text-sm text-gray-500 max-w-xl">
      <h3 className="text-base font-semibold text-[#32373c] mb-2">Wie funktioniert's?</h3>
      <ol className="space-y-1.5 list-decimal pl-5 mb-4">
        <li>Lies dem Anrufer die Einwilligung vor (links).</li>
        <li>Häkchen setzen, „Gespräch starten" klicken — Mikrofon geht an.</li>
        <li>Führe das Gespräch normal. Alle 15 Sekunden analysiert die KI deinen Live-Ausschnitt und zeigt hier Rückfragen, Produktempfehlungen, passende Wissensbausteine.</li>
        <li>Nach dem Gespräch: „Zusammenfassung erzeugen" → strukturierte Übergabe an Anfrage / Ticket.</li>
      </ol>
      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
        <strong>Datenschutz:</strong> Mikrofon läuft lokal im Browser. Audio wird kurz zur Transkription an den EES-Server geschickt und direkt wieder verworfen. Mitschrift bleibt im Arbeitsspeicher, bis du das Gespräch beendest. Persistent gespeichert wird nur die finale Zusammenfassung.
      </div>
    </div>
  );
}

function ContextChips({ live, summary }) {
  const caseType = summary?.caseType || live?.caseType;
  const families = live?.productFamilies || [];
  const cd = summary?.structuredData || live?.collectedData || {};
  return (
    <div className="space-y-3">
      {caseType && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Falltyp</div>
          <span className={`inline-block text-[11px] px-2 py-1 rounded-full border ${CASE_BG[caseType] || CASE_BG.mixed}`}
                data-testid="phone-case-type">
            {CASE_LABEL[caseType] || caseType}
          </span>
        </div>
      )}
      {families.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Produktfamilien</div>
          <div className="flex flex-wrap gap-1">
            {families.map(f => (
              <span key={f} className="text-[11px] px-2 py-0.5 rounded bg-[#9ec043]/10 text-[#7ca735] border border-[#9ec043]/30">
                {f}
              </span>
            ))}
          </div>
        </div>
      )}
      {Object.keys(cd).filter(k => cd[k] != null && cd[k] !== '').length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Eckdaten</div>
          <dl className="space-y-1 text-[11px]">
            {Object.entries(cd)
              .filter(([, v]) => v != null && v !== '')
              .slice(0, 10)
              .map(([k, v]) => (
                <div key={k} className="flex items-start gap-2">
                  <dt className="text-gray-400 w-[92px] shrink-0 capitalize">{k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())}</dt>
                  <dd className="text-[#32373c] flex-1 break-words">{String(v)}</dd>
                </div>
              ))}
          </dl>
        </div>
      )}
    </div>
  );
}

function LiveBlock({ live }) {
  if (!live || (!live.caseType && !live.suggestedQuestions?.length && !live.productRecommendation)) {
    return <p className="text-sm text-gray-400">Sobald der erste Ton-Ausschnitt ausgewertet ist, erscheinen hier Rückfragen, Produktempfehlungen und passende Wissensbausteine.</p>;
  }
  return (
    <>
      {live.fachpersonalRequired && (
        <Callout kind="danger" testid="phone-fachpersonal">
          Hinweis: Diese Maßnahme ist nur durch qualifiziertes Fachpersonal zulässig.
        </Callout>
      )}
      {live.productRecommendation && (
        <div>
          <BlockTitle icon={Sparkles} label="Produktempfehlung" />
          <div className="text-sm text-[#32373c] bg-[#9ec043]/10 border border-[#9ec043]/40 rounded p-3"
               data-testid="phone-product-recommendation">
            {live.productRecommendation}
          </div>
        </div>
      )}
      {(live.suggestedQuestions || []).length > 0 && (
        <div>
          <BlockTitle icon={HelpCircle} label="Empfohlene Rückfragen" />
          <ul className="space-y-1.5 text-sm text-[#32373c]" data-testid="phone-suggested-questions">
            {live.suggestedQuestions.map((q, i) => (
              <li key={i} className="flex gap-2 bg-blue-50 border border-blue-200 rounded p-2.5">
                <span className="text-blue-600 font-bold shrink-0">{i + 1}.</span>
                <span className="flex-1">{q}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(live.suggestedAnswers || []).length > 0 && (
        <div>
          <BlockTitle icon={MessageSquare} label="Antwort-Impulse" />
          <ul className="space-y-1.5 text-sm text-[#32373c]">
            {live.suggestedAnswers.map((a, i) => (
              <li key={i} className="bg-[#9ec043]/10 border border-[#9ec043]/30 rounded p-2.5">{a}</li>
            ))}
          </ul>
        </div>
      )}
      {(live.warnings || []).length > 0 && (
        <div>
          <BlockTitle icon={AlertTriangle} label="Hinweise" />
          <ul className="space-y-1 text-xs text-amber-700">
            {live.warnings.map((w, i) => <li key={i}>· {w}</li>)}
          </ul>
        </div>
      )}
      {(live.relevantKnowledge || []).length > 0 && (
        <div>
          <BlockTitle icon={BookOpen} label="Passende Wissensbausteine" />
          <ul className="space-y-1 text-xs text-gray-600">
            {live.relevantKnowledge.map((r, i) => <li key={i}>· {r}</li>)}
          </ul>
        </div>
      )}
    </>
  );
}

function SummaryBlock({ summary }) {
  return (
    <div className="space-y-4" data-testid="phone-summary">
      <div>
        <BlockTitle icon={Sparkles} label="Zusammenfassung" />
        <p className="text-sm text-[#32373c] leading-relaxed">{summary.shortSummary}</p>
      </div>
      {summary.productRecommendation && (
        <div>
          <BlockTitle icon={Sparkles} label="Produktempfehlung" />
          <p className="text-sm text-[#32373c] bg-[#9ec043]/10 border border-[#9ec043]/40 rounded p-3">
            {summary.productRecommendation}
          </p>
        </div>
      )}
      {(summary.openQuestions || []).length > 0 && (
        <div>
          <BlockTitle icon={HelpCircle} label="Offene Fragen" />
          <ul className="space-y-1 text-sm text-gray-700">
            {summary.openQuestions.map((q, i) => <li key={i}>· {q}</li>)}
          </ul>
        </div>
      )}
      {summary.nextActionSuggestion && (
        <div>
          <BlockTitle icon={MessageSquare} label="Nächster Schritt" />
          <p className="text-sm text-[#32373c] bg-gray-50 border border-gray-200 rounded p-3">
            {summary.nextActionSuggestion}
          </p>
        </div>
      )}
    </div>
  );
}

function BlockTitle({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="w-3.5 h-3.5 text-gray-400" />
      <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{label}</span>
    </div>
  );
}

function Callout({ kind, children, testid }) {
  const bg = kind === 'danger' ? 'bg-red-50 border-red-300 text-red-700' : 'bg-amber-50 border-amber-300 text-amber-700';
  return (
    <div className={`text-sm rounded p-3 border ${bg} flex gap-2`} data-testid={testid}>
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}
